import { useEffect } from 'react'
import { useStudioStore } from '@/store/studio-store'
import { toast } from '@/store/toast-store'
import { handleProjectCreateError } from '@/store/project-create-error'
import { ExploreGallery } from '@/features/project/components/explore-gallery'
import { PageHeader } from '@/components/page-header'
import { DashboardContainer } from '../components/dashboard-container'
import { openStudioLaunchChat } from '../lib/studio-launch-chat'
import { pushStudioPath, pushStudioRoute } from '@/lib/studio-navigation'
import { AppWindow, BarChart3, FileText, RefreshCcw, type LucideIcon } from 'lucide-react'
import { useStudioLocale } from '@/i18n'
import type { StartersCopy } from '@/i18n/studio-starters-copy'

/** Icon, tone and destination — the parts of a starter path that are not
 *  words. The title, description and CTA come from `starters.paths`, keyed by
 *  `copyKey`, so a locale cannot silently drop one. */
const STARTER_PATH_DEFS: {
  copyKey: keyof StartersCopy['paths']
  icon: LucideIcon
  tone: string
  action: 'new' | 'import' | 'library'
}[] = [
  { copyKey: 'app', icon: AppWindow, tone: 'text-sky-500', action: 'new' },
  { copyKey: 'document', icon: FileText, tone: 'text-violet-500', action: 'import' },
  { copyKey: 'report', icon: BarChart3, tone: 'text-cyan-500', action: 'import' },
  { copyKey: 'revision', icon: RefreshCcw, tone: 'text-orange-500', action: 'library' },
]

export function ExplorePage() {
  const projectList = useStudioStore((s) => s.projectList)
  const initProjectList = useStudioStore((s) => s.initProjectList)
  const openTemplateDetail = useStudioStore((s) => s.openTemplateDetail)
  const createServerProject = useStudioStore((s) => s.createServerProject)
  const setStudioRoute = useStudioStore((s) => s.setStudioRoute)
  const { strings } = useStudioLocale()
  const copy = strings.starters

  useEffect(() => {
    if (projectList.length === 0) initProjectList()
  }, [projectList.length, initProjectList])

  function handleStarterAction(action: 'new' | 'import' | 'library') {
    if (action === 'new') {
      pushStudioPath('/studio/new')
      return
    }

    setStudioRoute(action)
    pushStudioRoute(action)
  }

  return (
    <DashboardContainer className="p-6 space-y-6 max-w-6xl">
      <PageHeader
        route="templates"
        title={strings.navigation.starters}
        description={copy.pageDescription}
      />
      <section className="space-y-4" aria-label={copy.starterPaths}>
        <div className="mb-4 flex flex-col gap-1">
          <h2 className="text-sm font-semibold text-foreground">{copy.starterPaths}</h2>
          <p className="text-sm text-muted-foreground">{copy.starterPathsLead}</p>
        </div>
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          {STARTER_PATH_DEFS.map((path) => {
            const Icon = path.icon
            const text = copy.paths[path.copyKey]
            return (
              <article key={path.copyKey} className="flex min-h-44 flex-col rounded-md border border-border bg-background p-4">
                <div className={`mb-4 inline-flex h-10 w-10 items-center justify-center rounded-md border border-border bg-card ${path.tone}`}>
                  <Icon className="h-5 w-5" strokeWidth={1.8} aria-hidden />
                </div>
                <h3 className="text-sm font-semibold text-foreground">{text.title}</h3>
                <p className="mt-2 flex-1 text-sm leading-6 text-muted-foreground">{text.description}</p>
                <button
                  type="button"
                  onClick={() => handleStarterAction(path.action)}
                  className="mt-4 inline-flex h-10 items-center justify-center rounded-md border border-border bg-card px-3 text-sm font-medium text-foreground transition-colors hover:bg-muted"
                >
                  {text.cta}
                </button>
              </article>
            )
          })}
        </div>
      </section>
      <ExploreGallery
        templates={projectList}
        onOpenTemplate={openTemplateDetail}
        onUseTemplate={(templateId) => {
            const template = projectList.find((t) => t.id === templateId)
            const name = template?.name ?? copy.newAppFallbackName
            // persona-ux-04 — was fire-and-forget: a rejected create swallowed
            // its rejection (no signOut on auth expiry). Attach the shared
            // handler so expiry routes to sign-in and an unmarked error still
            // surfaces, without double-toasting the producer's marked toast.
            void createServerProject(name, templateId)
              .then(() => openStudioLaunchChat({
                source: 'template',
                projectName: name,
                templateName: template?.name,
              }))
              .catch((err) =>
                handleProjectCreateError(
                  err,
                  {
                    toastError: (m) => toast.error(m),
                    signOut: () => void useStudioStore.getState().signOut(),
                  },
                  copy.failedToStart,
                ),
              )
          }}
      />
    </DashboardContainer>
  )
}
